import { type ChangeEvent, memo, useCallback, useEffect, useState } from 'react';

import { Input } from '@air/primitive-input';

import { useSharePrivateCollectionModal } from '~/components/SharePrivateCollectionModal/SharePrivateCollectionModalContext';

export const CollectionNameSetting = memo(() => {
  const { collectionProps, updateCollectionName, saveCollectionName, isEditMode, errors, isSaving } =
    useSharePrivateCollectionModal();
  const [name, setName] = useState<string>(collectionProps.name);

  // Sync local state with context changes (e.g., after save)
  useEffect(() => {
    setName(collectionProps.name);
  }, [collectionProps.name]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      setName(newName);
      // Always update parent state immediately for validation feedback
      updateCollectionName(newName);
    },
    [updateCollectionName],
  );

  const handleBlur = useCallback(() => {
    const hasChanges = name !== collectionProps.name;
    const hasErrors = !name || !!errors?.name;

    // Only save if in edit mode, there are changes, and validation passes
    if (isEditMode && hasChanges && !hasErrors) {
      saveCollectionName?.();
    }
  }, [name, collectionProps.name, errors?.name, isEditMode, saveCollectionName]);

  return (
    <div>
      <div className="relative flex items-center">
        <Input
          size="large"
          value={name}
          data-testid="COLLECTION_NAME_SETTING_INPUT"
          onChange={handleChange}
          name="collectionName"
          className="w-full rounded-8"
          placeholder="Enter link name"
          onBlur={handleBlur}
          disabled={isSaving}
        />
      </div>
      {errors?.name && <p className="text-12 text-red-9">{errors.name}</p>}
    </div>
  );
});

CollectionNameSetting.displayName = 'CollectionNameSetting';
